/**
 * RoutesTableRow - Linha da tabela de rotas
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';

const RoutesTableRow = memo(({
  row,
  onDelete,
  selected = false,
  onSelect,
}) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${tokens?.accent?.primary}20`,
            color: tokens?.accent?.primary,
          }}
        >
          <RouteIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Device */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.deviceId || '-'}
        </Typography>
      </TableCell>

      {/* Created At */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <Tooltip title={t('sharedEdit')}>
          <IconButton
            size="small"
            onClick={() => navigate(`/settings/route/${row.id}`)}
            sx={{
              color: tokens?.text?.secondary,
              '&:hover': { color: tokens?.accent?.primary },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onDelete && (
          <Tooltip title={t('sharedRemove')}>
            <IconButton
              size="small"
              onClick={() => onDelete(row.id)}
              sx={{
                color: tokens?.text?.secondary,
                '&:hover': { color: tokens?.semantic?.error },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
});

RoutesTableRow.displayName = 'RoutesTableRow';

export default RoutesTableRow;
